import { getRandomUUID, waitFor } from '../helpers';
import { MarketingChannelService } from './marketing-channel-service';

const SESSION_IDENTITY_GRAPH_KEY = 'session_identity_graph';

const attribution = async () => {
	await ensureMarketingAttributionFallback();
	const persistent = await localGet('persistent_identity_graph') ?? {};
	const session = await sessionGet(SESSION_IDENTITY_GRAPH_KEY) ?? {};
	return {
		query_params: await sessionGet('queryParams') ?? {},
		...persistent,
		...session,
	};
};

const sessionGet = (key) => {
	return waitFor(() => {
		// Check if sessionStorage is available
		return typeof Storage !== 'undefined' && typeof window.sessionStorage !== 'undefined';
	}, 5000)
		.then(() => {
			try {
				const item = window.sessionStorage.getItem(key);
				return item ? JSON.parse(item) : null;
			} catch (error) {
				throw new Error(`Failed to get sessionStorage value for key "${key}": ${error.message}`);
			}
		})
		.catch(() => {
			console.warn(`SessionStorage is not available or timeout exceeded for key: ${key}`);
			return null;
		});
};

const sessionSet = (key, value) => {
	return waitFor(() => {
		// Check if sessionStorage is available
		return typeof Storage !== 'undefined' && typeof window.sessionStorage !== 'undefined';
	}, 5000)
		.then(() => {
			try {
				window.sessionStorage.setItem(key, JSON.stringify(value));
				return true;
			} catch (error) {
				throw new Error(`Failed to set sessionStorage value for key "${key}": ${error.message}`);
			}
		})
		.catch(() => {
			console.warn(`SessionStorage is not available or timeout exceeded for key: ${key}`);
			return false;
		});
};

const localGet = (key) => {
	return waitFor(() => {
		// Check if localStorage is available
		return typeof Storage !== 'undefined' && typeof window.localStorage !== 'undefined';
	}, 5000)
		.then(() => {
			try {
				const item = window.localStorage.getItem(key);
				return item ? JSON.parse(item) : null;
			} catch (error) {
				throw new Error(`Failed to get localStorage value for key "${key}": ${error.message}`);
			}
		})
		.catch(() => {
			console.warn(`LocalStorage is not available or timeout exceeded for key: ${key}`);
			return null;
		});
};

const getPageLoadUuid = (document) => {
	if (!document.pageLoadUuid) document.pageLoadUuid = getRandomUUID();
	return document.pageLoadUuid;
};

const ensureMarketingAttributionFallback = async () => {
	const sessionData = await sessionGet(SESSION_IDENTITY_GRAPH_KEY) ?? {};

	if (!sessionData.marketing_channel || !sessionData.marketing_sub_channel) {
		const marketingChannelService = new MarketingChannelService();
		const updates = { ...sessionData };

		if (!sessionData.marketing_channel) {
			const marketingChannelFromUrl = marketingChannelService.getMarketingChannel();

			if (marketingChannelFromUrl) {
				updates.marketing_channel = marketingChannelFromUrl;
			}
		}

		if (!sessionData.marketing_sub_channel) {
			const marketingSubChannelFromUrl = marketingChannelService.getMarketingSubChannelFromSessionStorage();

			if (marketingSubChannelFromUrl) {
				updates.marketing_sub_channel = marketingSubChannelFromUrl;
			}
		}

		if (
			updates.marketing_channel !== sessionData.marketing_channel ||
			updates.marketing_sub_channel !== sessionData.marketing_sub_channel
		) {
			await sessionSet(SESSION_IDENTITY_GRAPH_KEY, updates);
		}
	}
};

export { attribution, getPageLoadUuid };
